import axiosInstance from "../api/axiosInstance";
import API_ENDPOINTS from "../api/endpoints";

const AuthService = {
  /*
   * =======================================================
   * LOGIN
   * =======================================================
   *
   * Backend:
   *
   * 1. Validates email/password
   * 2. Creates access token
   * 3. Creates refresh token
   * 4. Stores both as HttpOnly cookies
   * 5. Returns user information
   *
   * Example:
   *
   * Set-Cookie:
   * accessToken=...; HttpOnly; Secure; SameSite=Lax
   *
   * Set-Cookie:
   * refreshToken=...; HttpOnly; Secure; SameSite=Lax
   */
  login: async ({ email, password, rememberMe }) => {
    const response = await axiosInstance.post(API_ENDPOINTS.AUTH.LOGIN, {
      email,
      password,
      rememberMe,
    });

    const payload = response.data?.data ?? response.data;

    if (!payload) {
      throw new Error("Invalid login response");
    }

    /*
     * IMPORTANT:
     *
     * Do NOT do:
     *
     * localStorage.setItem("token", ...)
     *
     * Do NOT put token into Redux.
     */

    return payload.user || payload;
  },

  /*
   * =======================================================
   * CURRENT USER
   * =======================================================
   *
   * This is called after F5.
   *
   * Browser automatically sends HttpOnly cookie.
   */
  getCurrentUser: async () => {
    const response = await axiosInstance.get(API_ENDPOINTS.AUTH.ME);

    return response.data?.data ?? response.data;
  },

  /*
   * =======================================================
   * REFRESH TOKEN
   * =======================================================
   *
   * No token is passed from JavaScript.
   *
   * Browser sends refreshToken HttpOnly cookie
   * automatically.
   *
   * Backend creates a new access cookie.
   */
  refreshToken: async () => {
    const response = await axiosInstance.post(
      API_ENDPOINTS.AUTH.REFRESH_TOKEN,
      {},
      {
        skipAuthRefresh: true,
      },
    );

    return response.data?.data ?? response.data;
  },

  /*
   * =======================================================
   * LOGOUT
   * =======================================================
   *
   * Backend clears HttpOnly cookies.
   */
  logout: async () => {
    try {
      await axiosInstance.post(
        API_ENDPOINTS.AUTH.LOGOUT,
        {},
        {
          skipAuthRefresh: true,
        },
      );
    } catch (error) {
      /*
       * Let caller decide how to handle logout errors.
       */
      throw error;
    }
  },

  forgotPassword: (data) =>
    axiosInstance.post(API_ENDPOINTS.AUTH.ForgotPassword, data),

  resetPassword: (data) =>
    axiosInstance.post(API_ENDPOINTS.AUTH.ResetPassword, data),

  changePassword: (data) =>
    axiosInstance.post(API_ENDPOINTS.AUTH.ChangePassword, data),

  /*
   * These are intentionally no longer used.
   *
   * Kept only so old components don't immediately break.
   */
  getToken: () => null,

  getRefreshToken: () => null,

  isAuthenticated: () => false,
};

export default AuthService;
